// First-visit help overlay — shows once after login; re-openable via the ? button.
// Content is loaded from settings/app.helpMarkdown in Firestore.
import { el } from "../lib/dom";
import { watchSettings } from "../lib/data";

const SEEN_KEY = "riverghost_help_seen";

/** Convert a small subset of Markdown to safe HTML (no external deps). */
function markdownToHtml(md: string): string {
  function inline(text: string): string {
    return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  }

  const lines = md.split("\n");
  const parts: string[] = [];
  let inList = false;

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      if (inList) { parts.push("</ul>"); inList = false; }
      parts.push(`<h3>${inline(line.slice(3))}</h3>`);
    } else if (line.startsWith("- ")) {
      if (!inList) { parts.push("<ul>"); inList = true; }
      parts.push(`<li>${inline(line.slice(2))}</li>`);
    } else {
      if (inList) { parts.push("</ul>"); inList = false; }
      parts.push(line ? `${inline(line)}<br>` : "<br>");
    }
  }
  if (inList) parts.push("</ul>");
  return parts.join("");
}

let currentMarkdown = "";
let unsubSettings: (() => void) | null = null;

/** Subscribe to settings/app.helpMarkdown once (module-level singleton). */
function ensureSubscribed(): void {
  if (unsubSettings) return;
  unsubSettings = watchSettings((s) => {
    currentMarkdown = s?.helpMarkdown ?? "";
  });
}

/** Build and attach the overlay to document.body. */
function buildOverlay(forced: boolean): void {
  if (!forced && localStorage.getItem(SEEN_KEY)) return;

  const overlay = el("div", {
    style: "position:fixed;inset:0;z-index:3000;background:rgba(4,6,9,.95);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);overflow-y:auto;padding:20px",
  });

  const scanline = el("div", {
    style: "position:fixed;inset:0;pointer-events:none;z-index:1;background:repeating-linear-gradient(to bottom,transparent,transparent 3px,rgba(0,0,0,.08) 3px,rgba(0,0,0,.08) 4px)",
  });
  overlay.append(scanline);

  const body = el("div", {
    style: "max-width:480px;margin:0 auto;padding-bottom:40px;font-family:var(--mono);color:var(--cream);position:relative;z-index:2",
  });

  const tag = el("span", {
    style: "display:block;font-size:9px;letter-spacing:.15em;text-transform:uppercase;color:var(--amber);opacity:.7;margin-bottom:18px;margin-top:4px",
  }, "OPERATION RIVERGHOST // FIELD BRIEF");

  const styleTag = document.createElement("style");
  styleTag.textContent = `
    .rg-help h3 { color:var(--amber);font-family:var(--mono);font-size:11px;letter-spacing:.14em;text-transform:uppercase;margin:22px 0 8px;border-bottom:1px solid var(--line);padding-bottom:4px }
    .rg-help strong { color:var(--amber);font-weight:600 }
    .rg-help ul { margin:6px 0 6px 16px;padding:0;list-style:disc }
    .rg-help li { margin-bottom:4px }
  `;
  document.head.append(styleTag);

  const markdownWrap = el("div", { class: "rg-help", style: "line-height:1.7;font-size:14px" });
  if (currentMarkdown) {
    markdownWrap.innerHTML = markdownToHtml(currentMarkdown);
  } else {
    markdownWrap.innerHTML = [
      "<h3>MISSION BRIEFING</h3>",
      "Welcome, Agent. Your mission unfolds here on <strong>Donauinsel</strong>.<br><br>",
      "Crack <strong>case files</strong> and <strong>cache riddles</strong> to earn <strong>intel points</strong> and hunt the water-gun caches hidden on the island.<br><br>",
      "<h3>FIELD PROTOCOL</h3>",
      "<ul>",
      "<li>Open <strong>Cases</strong> and solve each dossier riddle for intel.</li>",
      "<li>On the <strong>Map</strong>, crack a cache's riddle to pin its spot — or spend intel to buy the pin or a hint.</li>",
      "<li>Reach the spot, find the water gun, and claim it with its code + a selfie.</li>",
      "<li>Every agent with tracking on shows live on the map — you can see everyone.</li>",
      "</ul>",
      "<h3>COMMS</h3>",
      "Enable <strong>secure comms</strong> (notifications) and <strong>field tracking</strong> (GPS) from your Agent profile to stay operational.<br>",
    ].join("");
  }

  const closeBtn = el("button.btn", { style: "margin-top:32px;width:100%" }, "Got it, Agent") as HTMLButtonElement;
  closeBtn.addEventListener("click", () => {
    localStorage.setItem(SEEN_KEY, "1");
    overlay.remove();
    styleTag.remove();
  });

  body.append(tag, markdownWrap, closeBtn);
  overlay.append(body);
  document.body.append(overlay);
}

/**
 * Show the help overlay.
 * @param forced — if true, always show even if already seen.
 */
export function showHelpOverlay(forced = false): void {
  ensureSubscribed();
  buildOverlay(forced);
}
